import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { Router } from '@angular/router'

import { LaskuToimittaja, ListausLaskuToimittaja } from '@lemon/core-firebase/model/lasku'

import { MaksutToimittajatFirestoreDataSource, ToimittajaSort } from './maksut-toimittajat.firestore.datasource'

import { MaksuToimittajaDataResolve } from 'app/_angular/_resolvers/maksu.resolve'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Observable, Subject, combineLatest } from 'rxjs'
import { debounceTime, map, takeUntil } from 'rxjs/operators'

interface ToimittajahakuLomake {
  nimi: string
  start: Date | null
  end: Date | null
}

@Component({
  selector: 'app-toimittajat',
  templateUrl: './maksut-toimittajat.component.html',
  styleUrls: ['./maksut-toimittajat.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksutToimittajatComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private _basePath: string

  @ViewChild(MatPaginator, { static: true }) _paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) _sort: MatSort

  lomake: FieldTree<ToimittajahakuLomake>

  naytaEiYhtaanObservable: Observable<boolean>
  naytettavatObservable: Observable<ListausLaskuToimittaja[]>
  lataaObservable: Observable<boolean>

  propertiesToDisplay = ['numero', 'nimi', 'yhteensa', 'maksamatta']
  dataSource: MaksutToimittajatFirestoreDataSource

  constructor(
    private _componentDataResolve: MaksuToimittajaDataResolve,
    private _router: Router,
    private _dataSource: MaksutToimittajatFirestoreDataSource,
  ) {
    const currentUrl = this._router.url
    this._basePath = currentUrl.startsWith('/tositteet') ? '/tositteet/toimittajat' : '/maksut/toimittajat'
    this.dataSource = this._dataSource
    this.lataaObservable = this._dataSource.lataaObservable
    this.naytettavatObservable = this._dataSource.naytettavatObservable
    this.naytaEiYhtaanObservable = combineLatest([this._dataSource.rivienMaaraObservable, this._dataSource.lataaObservable]).pipe(
      map(([rivienMaara, lataa]) => {
        if (lataa) {
          return false
        }
        return rivienMaara < 1
      }),
    )

    this.lomake = form(
      signal<ToimittajahakuLomake>({
        nimi: this._dataSource.getSearch() ?? '',
        start: this._dataSource.getStart(),
        end: this._dataSource.getEnd(),
      }),
    )

    arvonMuutokset(this.lomake.nimi)
      .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value) {
          const val = value.trim().toLowerCase()
          this._dataSource.changeSearch(val)
        } else {
          this._dataSource.changeSearch(null)
        }
      })

    arvonMuutokset(this.lomake.start)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value instanceof Date) {
          this._dataSource.changeStart(value)
        } else {
          this._dataSource.changeStart(null)
        }
      })

    arvonMuutokset(this.lomake.end)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value instanceof Date) {
          this._dataSource.changeEnd(value)
        } else {
          this._dataSource.changeEnd(null)
        }
      })
  }

  ngOnInit() {
    const currentSort = this._dataSource.getSort()
    this._sort.sort({
      id: currentSort.active,
      disableClear: true,
      start: currentSort.direction === 'asc' ? 'asc' : 'desc',
    })
    this._sort.sortChange.subscribe(sort => {
      this._dataSource.changeSort(sort as ToimittajaSort)
    })

    this._paginator.page.subscribe(page => {
      this._dataSource.changePage(page)
    })
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  muokkaa(data: LaskuToimittaja) {
    this._router.navigate([this._basePath, data.avain])
  }

  lisaa() {
    this._router.navigate([this._basePath, 'uusi'])
  }
}
